import { z } from 'zod'
import { MemberInvitationDto } from '../membership'
import {
  AreasOfInterestEnumValues,
  BaseLayerTypeEnumValues,
  ContactTypeEnumValues,
  LeadSourceTypeEnumValues,
  OrganizationTypeEnum,
  SectorTypeEnumValues,
  type BaseLayerTypeEnumType,
  type SectorTypeEnumType,
} from '@forgd/shared/enums'
import { FORGD_SYSTEM_COINGECKO_ID } from '@forgd/shared/constants'

const nullableString = z
  .string()
  .nullable()
  .transform((val) => (!val ? null : val))
  .optional()

const baseUserDetailsFields = {
  firstName: z.string().trim().min(1, { message: 'First Name is required.' }),
  lastName: z.string().trim().min(1, { message: 'Last Name is required.' }),
  telegram: z.string().trim().min(1, { message: 'Telegram username is required.' }),
  contactType: z.enum(ContactTypeEnumValues),
  organizationName: z.string().trim().min(1, { message: 'Field is required.' }),
  position: z.string().trim().min(1, { message: 'Position is required.' }),
  leadSourceType: z.enum(LeadSourceTypeEnumValues),
}

export const OwnerDetailsBody = z.object(baseUserDetailsFields).extend({
  projectId: z.string().uuid(),
})

export const AdvisorDetailsBody = z.object(baseUserDetailsFields).omit({
  organizationName: true,
})

export const OrganizationDetailsBody = z.object({
  name: z.string().trim().min(1, { message: 'Name is required.' }),
  link: z.string().url({ message: 'Organization website should be a valid URL. (e.g. https://www.yourcompany.com)' }),
  image: z
    .string({ message: 'Organization image is required.' })
    .url({ message: 'Organization image is required.' })
    .nullable(),
})

export const UpdateListedProjectBody = z.object({
  tokenListed: z.literal(true),
  coingeckoId: z
    .string()
    .nullable()
    .refine((val) => (val && val.trim() === FORGD_SYSTEM_COINGECKO_ID ? false : true), {
      message: 'Coingecko cannot be system token',
    }),
  image: z
    .string()
    .nullable()
    .refine((val) => val !== null && val.trim() !== '', {
      message: 'Token Profile Image is required',
    }),
  name: z.string().trim().min(1, { message: 'Token name is required.' }),
  ticker: z
    .string()
    .trim()
    .min(1, { message: 'Token ticker is required.' })
    .transform((val) => val.toUpperCase())
    .refine((val) => val !== FORGD_SYSTEM_COINGECKO_ID, {
      message: 'Token ticker cannot be system token',
    }),
  tgeDate: z.string().min(1, { message: 'TGE Date is required.' }).date(),
  link: z.string().url({ message: 'Token website should be a valid URL.' }),
  sector: z.array(z.enum(SectorTypeEnumValues)).nonempty({ message: 'Project Sector is required.' }),
  baseLayer: z.array(z.enum(BaseLayerTypeEnumValues)).nonempty({ message: 'Base Layer is required.' }),
})

export const UpdateNotListedProjectBody = z.object({
  tokenListed: z.literal(false),
  coingeckoId: z
    .string()
    .nullable()
    .optional()
    .refine((val) => val?.trim() !== FORGD_SYSTEM_COINGECKO_ID, 'Coingecko cannot be system token')
    .transform((val) => (!val ? null : val)),
  image: nullableString,
  name: z.string().trim().min(1, { message: 'Token name is required.' }),
  ticker: z
    .string()
    .nullable()
    .transform((val) => {
      if (!val || val.trim() === '') {
        return null
      }
      return val.toUpperCase()
    }),
  tgeDate: z.string().date().nullable().optional(),
  link: z
    .union([
      z.string().url({ message: 'Token website should be a valid URL.' }),
      z.string().nullable(),
      z.string().length(0),
    ])
    .transform((val) => {
      if (!val || val.trim() === '') {
        return null
      }

      return val
    }),
  sector: z.array(z.enum(SectorTypeEnumValues)).min(0).optional(),
  baseLayer: z.array(z.enum(BaseLayerTypeEnumValues)).min(0).optional(),
})

export const OnboardingProjectBody = z.discriminatedUnion('tokenListed', [
  UpdateListedProjectBody,
  UpdateNotListedProjectBody,
])

export const CompleteProjectOnboardingBody = z.object(baseUserDetailsFields).extend({
  organizationId: z.string().uuid(),
  isResearch: z.boolean(),
  organizationName: z.string(),
  contactType: z.enum(ContactTypeEnumValues),
  telegram: z.string().min(1),
  project: OnboardingProjectBody.nullable(),
  members: z.array(MemberInvitationDto),
  areasOfInterest: z.array(z.enum(AreasOfInterestEnumValues)),
})

export const CompleteAdvisorOnboardingBody = z.object({
  ownerDetails: AdvisorDetailsBody,
  organization: OrganizationDetailsBody.extend({
    areasOfInterest: z.array(z.enum(AreasOfInterestEnumValues)),
  }),
})

export const FinalizeRegistrationBody = z.object({
  firstName: z.string().trim().min(1, { message: 'First Name is required.' }),
  lastName: z.string().trim().min(1, { message: 'Last Name is required.' }),
  telegram: z.string().trim().optional(),
  tokenStatus: z.enum(['pre-tge', 'listed', 'no-token']),
  coingeckoId: z.string().nullable().optional(),
  projectName: z.string().trim().optional(),
  ticker: z.string().trim().optional(),
  baseLayer: z.array(z.string()).optional(),
  tgeDate: z.string().nullable().optional(),
})

export type FinalizeRegistrationDetails = z.infer<typeof FinalizeRegistrationBody>

export const MemberDetailsBody = OwnerDetailsBody.omit({
  leadSourceType: true,
}).extend({
  email: z
    .string()
    .email()
    .transform((email) => email.toLowerCase()),
})

export const BaseUpdateMemberDetailsBody = z.object({
  firstName: z.string().trim().min(1, { message: 'First Name is required.' }),
  lastName: z.string().trim().min(1, { message: 'Last Name is required.' }),
  contactType: z.enum(ContactTypeEnumValues),
  telegram: z.string().trim().min(1, { message: 'Telegram is required.' }),
  organizationId: z.string().uuid(),
})

export const UpdateProjectMemberDetailsBody = BaseUpdateMemberDetailsBody.extend({
  organizationType: z.literal(OrganizationTypeEnum.Project),
  position: z.string().min(1, { message: 'Position is required.' }),
  organizationName: z.string().min(1, { message: 'Organization Name is required' }),
})

export const UpdateAdvisorMemberDetailsBody = BaseUpdateMemberDetailsBody.extend({
  organizationType: z.literal(OrganizationTypeEnum.Advisor),
})

export const UpdateMemberDetailsBody = z.discriminatedUnion('organizationType', [
  UpdateProjectMemberDetailsBody,
  UpdateAdvisorMemberDetailsBody,
])

export type OwnerDetails = z.infer<typeof OwnerDetailsBody>
export type MemberDetails = z.infer<typeof MemberDetailsBody>
export type UpdateMemberDetails = z.infer<typeof UpdateMemberDetailsBody>
export type CompleteAdvisorOnboardingDetails = z.infer<typeof CompleteAdvisorOnboardingBody>
export type CompleteProjectOnboardingDetails = z.infer<typeof CompleteProjectOnboardingBody>
export type OrganizationDetails = z.infer<typeof OrganizationDetailsBody>

// to prevent typescript errors sector and baseLayer should have min 0 and tokenListed boolean
export type ListedTokenDetails = Omit<z.infer<typeof UpdateListedProjectBody>, 'tokenListed'> & {
  sector: SectorTypeEnumType[]
  baseLayer: BaseLayerTypeEnumType[]
  tokenListed: boolean
}
export type UnlistedTokenDetails = Omit<z.infer<typeof UpdateNotListedProjectBody>, 'tokenListed'> & {
  tokenListed: boolean
}

export type OnboardingTokenDetails = ListedTokenDetails | UnlistedTokenDetails
